import { Component, AfterViewInit, ViewChild, ElementRef, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { createChart, AreaSeries, LineSeries, DeepPartial, ChartOptions, ISeriesApi, UTCTimestamp, IChartApi } from 'lightweight-charts';
import { CommonModule } from '@angular/common';

interface ChartSeries {
  time: UTCTimestamp;
  value: number;
}

interface PriceHistoryData {
  yes: ChartSeries[];
  no: ChartSeries[];
  xAxisGrid?: string[];
  yAxisGrid?: number[];
  xAxisInterval?: string;
}

@Component({
  selector: 'app-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.css']
})
export class ChartComponent implements AfterViewInit, OnChanges {
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  @ViewChild('tooltip') tooltip!: ElementRef;

  @Input() chartData!: PriceHistoryData;
  @Input() showControls: boolean = true;
  // Mobile (<= 575px) only: render the interval bar under the graph
  @Input() controlsBelow: boolean = false;
  @Input() chartType: 'market' | 'profit' = 'market';
  @Input() activeInterval = 'ALL';
  @Input() yesTitle: string = 'Yes';
  @Input() noTitle: string = 'No';
  @Output() intervalChange = new EventEmitter<string>();

  private chart!: IChartApi;
  private yesSeries!: ISeriesApi<any>;
  private noSeries!: ISeriesApi<any>;
  private priceLines: any[] = [];

  currentYes: string = '0';
  currentNo: string = '0';

  intervals = ['1H', '6H', '1D', '1W', '1M', 'ALL'];
  ngAfterViewInit(): void {
    const chartOptions: DeepPartial<ChartOptions> = {
      width: this.chartContainer.nativeElement.clientWidth,
      height: this.chartType === 'profit' ? 100 : 400,
      handleScroll: false,
      handleScale: false,
      layout: {
        background: { color: '#ffffff' },
        textColor: '#333',
        attributionLogo: false
      },
      grid: {
        vertLines: { visible: true, color: '#f0f3fa' },
        horzLines: { visible: false }
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true
      },
      rightPriceScale: {
        borderVisible: false,
        textColor: '#333',
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      localization: {
        priceFormatter: (price: number) => this.formatAxisValue(price)
      },
      crosshair: {
        horzLine: { visible: false, labelVisible: false },
        vertLine: { labelVisible: false, style: 2 }
      }
    };

    this.chart = createChart(this.chartContainer.nativeElement, {
      ...chartOptions,
      width: this.chartContainer.nativeElement.clientWidth,
      height: this.chartType === 'profit' ? 100 : 400,
    });

    if (this.chartType === 'profit') {
      this.yesSeries = this.chart.addSeries(AreaSeries, {
        lineColor: '#5B44E6',
        topColor: 'rgba(91, 68, 230, 0.4)',
        bottomColor: 'rgba(91, 68, 230, 0.0)',
        lineWidth: 2,
        priceFormat: {
          type: 'custom',
          formatter: (price: number) => this.formatAxisValue(price)
        },
        priceLineVisible: false,
        lastValueVisible: false
      });

      this.noSeries = this.chart.addSeries(AreaSeries, {
        lineColor: '#dc2626',
        topColor: 'rgba(220, 38, 38, 0.3)',
        bottomColor: 'rgba(220, 38, 38, 0.0)',
        lineWidth: 2,
        priceFormat: {
          type: 'custom',
          formatter: (price: number) => this.formatAxisValue(price)
        },
        priceLineVisible: false,
        lastValueVisible: false,
        visible: false
      });
    } else {
      this.yesSeries = this.chart.addSeries(LineSeries, {
        color: '#16a34a',
        lineWidth: 2,
        priceFormat: {
          type: 'custom',
          formatter: (price: number) => this.formatAxisValue(price)
        },
        priceLineVisible: false,
        lastValueVisible: false,
        lineType: 2 // 2 is WithSteps for sharp 
      });

      this.noSeries = this.chart.addSeries(LineSeries, {
        color: '#dc2626',
        lineWidth: 2,
        priceFormat: {
          type: 'custom',
          formatter: (price: number) => this.formatAxisValue(price)
        },
        priceLineVisible: false,
        lastValueVisible: false,
        lineType: 2
      });
    }

    this.setupTooltip();

    window.addEventListener('resize', () => {
      this.chart.applyOptions({ width: this.chartContainer.nativeElement.clientWidth });
    });

    if (this.chartData) this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] && this.chart && this.chartData) {
      this.renderChart();
    }
  }

  setChartInterval(interval: string) {
    this.activeInterval = interval;
    this.intervalChange.emit(interval);
    if (this.chartData) this.updateTimeScale(this.chartData);
  }

  private renderChart() {
    if (!this.chartData) return;

    if (this.chartType === 'profit') {
      const profitPoints = this.chartData.yes || [];
      const latestProfit = profitPoints[profitPoints.length - 1]?.value || 0;
      const isProfit = latestProfit >= 0;
      this.yesSeries.applyOptions({
        lineColor: isProfit ? '#16a34a' : '#dc2626',
        topColor: isProfit ? 'rgba(22, 163, 74, 0.4)' : 'rgba(220, 38, 38, 0.4)',
        bottomColor: isProfit ? 'rgba(22, 163, 74, 0.0)' : 'rgba(220, 38, 38, 0.0)'
      });
    }

    this.yesSeries.setData(this.chartData.yes || []);
    this.noSeries.setData(this.chartData.no || []);

    // Remove old custom grid lines
    this.priceLines.forEach(line => this.yesSeries.removePriceLine(line));
    this.priceLines = [];
    // Dynamic Y-axis grid
    if (this.chartData.yAxisGrid) {
      const grid = this.chartData.yAxisGrid;
      const minVal = Math.min(...grid);
      const maxVal = Math.max(...grid);

      grid.forEach((price: number) => {
        const line = this.yesSeries.createPriceLine({
          price,
          color: '#f0f3fa',
          lineWidth: 1,
          lineStyle: 1,
          axisLabelVisible: true
        });
        this.priceLines.push(line);
      });

      this.yesSeries.applyOptions({
        autoscaleInfoProvider: () => ({
          priceRange: { minValue: minVal, maxValue: maxVal },
          margins: { above: 10, below: 10 }
        })
      });

      this.noSeries.applyOptions({
        autoscaleInfoProvider: () => ({
          priceRange: { minValue: minVal, maxValue: maxVal },
          margins: { above: 10, below: 10 }
        })
      });

      this.chart.applyOptions({
        rightPriceScale: {
          autoScale: true,
          scaleMargins: { top: 0.1, bottom: 0.1 },
        }
      });
    }

    // Set X-axis range from backend timestamps
    this.updateTimeScale(this.chartData);

    this.resetLegendToLatest();
  }

  private updateTimeScale(data: PriceHistoryData) {
    if (!data.yes?.length) return;

    const intervalStr = data.xAxisGrid?.length
      ? data.xAxisInterval || '1d'
      : '1d';

    const intervalValue = parseInt(intervalStr); // 3 from "3d"

    const firstPoint = data.yes[0].time;

    this.chart.timeScale().fitContent();

    this.chart.applyOptions({
      timeScale: {
        timeVisible: true,
        tickMarkFormatter: (time: UTCTimestamp) => {
          const date = new Date(time * 1000);

          switch (this.activeInterval) {
            case '1H':
            case '6H':
            case '1D':
              return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            case '1W':
            case '1M':
            case 'ALL':
            default:
              return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
          }
        }
      }
    });
  }

  private setupTooltip(): void {
    this.chart.subscribeCrosshairMove((param: any) => {
      const toolTipEl = this.tooltip.nativeElement;
      const container = this.chartContainer.nativeElement;

      if (!param.point || !param.time || param.point.x < 0 || param.point.x > container.clientWidth) {
        toolTipEl.style.display = 'none';
        this.resetLegendToLatest();
        return;
      }

      toolTipEl.style.display = 'block';

      const yesData = param.seriesData.get(this.yesSeries);
      const noData = param.seriesData.get(this.noSeries);

      this.currentYes = yesData ? yesData.value.toFixed(2) : '0';
      this.currentNo = noData ? noData.value.toFixed(2) : '0';

      const dateObj = new Date(param.time * 1000);
      const isShort = ['1H', '6H', '1D'].includes(this.activeInterval.toUpperCase());
      const dateStr = isShort
        ? `${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}`
        : dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

      if (this.chartType === 'profit') {
        const val = parseFloat(this.currentYes);
        const prefix = val >= 0 ? '+$' : '-$';
        const formattedVal = Math.abs(val).toFixed(2);
        toolTipEl.innerHTML = `
          <div style="color: #666; font-size: 12px; font-family: 'Syne', sans-serif; margin-bottom: 4px;">${dateStr}</div>
          <div style="color: ${val >= 0 ? '#16a34a' : '#dc2626'}; font-weight: bold;">
            ${prefix}${formattedVal}
          </div>
        `;
      } else {
        toolTipEl.innerHTML = `
          <div style="color: #666; font-size: 12px; margin-bottom: 4px;">${dateStr}</div>
          <div style="color: #16a34a">${this.yesTitle}: ${parseFloat(this.currentYes).toFixed(0)}%</div>
          <div style="color: #dc2626">${this.noTitle}: ${parseFloat(this.currentNo).toFixed(0)}%</div>
        `;
      }

      let left = param.point.x + 15;
      let top = param.point.y + 15;
      if (left + 120 > container.clientWidth) left = param.point.x - 135;
      if (top + 50 > container.clientHeight) top = param.point.y - 65;
      toolTipEl.style.left = `${left}px`;
      toolTipEl.style.top = `${top}px`;
    });
  }

  private resetLegendToLatest(): void {
    if (!this.chartData?.yes?.length || !this.chartData?.no?.length) return;

    const lastYes = this.chartData.yes[this.chartData.yes.length - 1]?.value || 0;
    const lastNo = this.chartData.no[this.chartData.no.length - 1]?.value || 0;

    this.currentYes = lastYes.toFixed(0);
    this.currentNo = lastNo.toFixed(0);
  }

  private formatAxisValue(price: number): string {
    if (this.chartType === 'profit') {
      const prefix = price >= 0 ? '$' : '-$';
      return `${prefix}${Math.abs(price).toFixed(2)}`;
    }

    return `${Number(price).toFixed(0)}%`;
  }
}
